/**
 * WebSocket Message Types
 * Central place to define all WebSocket message types used across the application
 */

export const WEBSOCKET_MESSAGE_TYPES = {
    // Connection types
    PING: 'ping',
    PONG: 'pong',

    // Transaction: Payment
    TRANSACTION_PAYMENT_APPROVED: 'TRANSACTION:PAYMENT:APPROVED',
    TRANSACTION_PAYMENT_REJECTED: 'TRANSACTION:PAYMENT:REJECTED',
    TRANSACTION_PAYMENT_COMPLETED: 'TRANSACTION:PAYMENT:COMPLETED',

    // Transaction: Withdrawal
    TRANSACTION_WITHDRAWAL_APPROVED: 'TRANSACTION:WITHDRAWAL:APPROVED',
    TRANSACTION_WITHDRAWAL_REJECTED: 'TRANSACTION:WITHDRAWAL:REJECTED',
    TRANSACTION_WITHDRAWAL_COMPLETED: 'TRANSACTION:WITHDRAWAL:COMPLETED',

    // Transaction: Receipt
    TRANSACTION_RECEIPT_APPROVED: 'TRANSACTION:RECEIPT:APPROVED',
    TRANSACTION_RECEIPT_REJECTED: 'TRANSACTION:RECEIPT:REJECTED',
    TRANSACTION_RECEIPT_COMPLETED: 'TRANSACTION:RECEIPT:COMPLETED',

    // Transaction: Journal
    TRANSACTION_JOURNAL_APPROVED: 'TRANSACTION:JOURNAL:APPROVED',
    TRANSACTION_JOURNAL_REJECTED: 'TRANSACTION:JOURNAL:REJECTED',
    TRANSACTION_JOURNAL_COMPLETED: 'TRANSACTION:JOURNAL:COMPLETED',

    // Transaction: Cancellation
    TRANSACTION_CANCELLATION_COMPLETED: 'TRANSACTION:CANCELLATION:COMPLETED',

    // Transaction: FD Closure
    TRANSACTION_FD_CLOSURE_COMPLETED: 'TRANSACTION:FD_CLOSURE:COMPLETED',

    // Transaction: Approval
    TRANSACTION_APPROVAL_PENDING: 'TRANSACTION:APPROVAL:PENDING',

    // Process: Day End
    PROCESS_DAY_END_PROCESSING: 'PROCESS:DAY_END:PROCESSING',
    PROCESS_DAY_END_COMPLETED: 'PROCESS:DAY_END:COMPLETED',

    // Process: Month End
    PROCESS_MONTH_END_PROCESSING: 'PROCESS:MONTH_END:PROCESSING',
    PROCESS_MONTH_END_COMPLETED: 'PROCESS:MONTH_END:COMPLETED',

    // Process: Year End
    PROCESS_YEAR_END_PROCESSING: 'PROCESS:YEAR_END:PROCESSING',
    PROCESS_YEAR_END_COMPLETED: 'PROCESS:YEAR_END:COMPLETED',

    // Process: Month Close
    PROCESS_MONTH_CLOSE_PROCESSING: 'PROCESS:MONTH_CLOSE:PROCESSING',
    PROCESS_MONTH_CLOSE_COMPLETED: 'PROCESS:MONTH_CLOSE:COMPLETED',

    // Process: Backfill
    PROCESS_BACKFILL_PROCESSING: 'PROCESS:BACKFILL:PROCESSING',
    PROCESS_BACKFILL_COMPLETED: 'PROCESS:BACKFILL:COMPLETED',

    // FD Advance
    FD_ADVANCE_APPLICATION_SUBMITTED: 'FD_ADVANCE:APPLICATION:SUBMITTED',

    // Report
    REPORT_PROGRESS: 'REPORT:PROGRESS',

    // Notifications
    NOTIFICATION: 'NOTIFICATION',

    // System
    SYSTEM_UPDATE: 'SYSTEM:UPDATE',
    SYSTEM_LOG_CREATED: 'SYSTEM:LOG_CREATED',

    // Transaction: Loan Disbursement
    TRANSACTION_LOAN_DISBURSEMENT_APPROVED: 'TRANSACTION:LOAN_DISBURSEMENT:APPROVED',
    TRANSACTION_LOAN_DISBURSEMENT_REJECTED: 'TRANSACTION:LOAN_DISBURSEMENT:REJECTED',
    TRANSACTION_LOAN_DISBURSEMENT_COMPLETED: 'TRANSACTION:LOAN_DISBURSEMENT:COMPLETED',
    TRANSACTION_AUTO_RECOVERY_COMPLETED: 'TRANSACTION:AUTO_RECOVERY:COMPLETED',
    TRANSACTION_CHILDREN_TRANSFER_COMPLETED: 'TRANSACTION:CHILDREN_TRANSFER:COMPLETED',
    TRANSACTION_LEGAL_ACTION_LOAN_COMPLETED: 'TRANSACTION:LEGAL_ACTION_LOAN:COMPLETED',
    TRANSACTION_MOBILE_POS_COMPLETED: 'TRANSACTION:MOBILE_POS:COMPLETED',

    // Transaction: Equipment
    TRANSACTION_EQUIPMENT_APPROVED: 'TRANSACTION:EQUIPMENT:APPROVED',
    TRANSACTION_EQUIPMENT_REJECTED: 'TRANSACTION:EQUIPMENT:REJECTED',
    TRANSACTION_EQUIPMENT_COMPLETED: 'TRANSACTION:EQUIPMENT:COMPLETED',

    // Transaction: Investment
    TRANSACTION_INVESTMENT_APPROVED: 'TRANSACTION:INVESTMENT:APPROVED',
    TRANSACTION_INVESTMENT_REJECTED: 'TRANSACTION:INVESTMENT:REJECTED',
    TRANSACTION_INVESTMENT_COMPLETED: 'TRANSACTION:INVESTMENT:COMPLETED',

    // Transaction: Invoice
    TRANSACTION_INVOICE_APPROVED: 'TRANSACTION:INVOICE:APPROVED',
    TRANSACTION_INVOICE_REJECTED: 'TRANSACTION:INVOICE:REJECTED',
    TRANSACTION_INVOICE_COMPLETED: 'TRANSACTION:INVOICE:COMPLETED',
    // Transaction: Purchase
    TRANSACTION_PURCHASE_APPROVED: 'TRANSACTION:PURCHASE:APPROVED',
    TRANSACTION_PURCHASE_REJECTED: 'TRANSACTION:PURCHASE:REJECTED',
    TRANSACTION_PURCHASE_COMPLETED: 'TRANSACTION:PURCHASE:COMPLETED',

    // Transaction: Pay to Creditor
    TRANSACTION_PAY_TO_CREDITOR_APPROVED: 'TRANSACTION:PAY_TO_CREDITOR:APPROVED',
    TRANSACTION_PAY_TO_CREDITOR_REJECTED: 'TRANSACTION:PAY_TO_CREDITOR:REJECTED',
    TRANSACTION_PAY_TO_CREDITOR_COMPLETED: 'TRANSACTION:PAY_TO_CREDITOR:COMPLETED',

    // Import: MDB
    IMPORT_MDB_PROCESSING: 'IMPORT:MDB:PROCESSING',
    IMPORT_MDB_COMPLETED: 'IMPORT:MDB:COMPLETED',
    IMPORT_MDB_FAILED: 'IMPORT:MDB:FAILED',
    IMPORT_MDB_CLEARED: 'IMPORT:MDB:CLEARED',

    // Import: Validation
    IMPORT_VALIDATION_PROCESSING: 'IMPORT:VALIDATION:PROCESSING',
    IMPORT_VALIDATION_COMPLETED: 'IMPORT:VALIDATION:COMPLETED',
    IMPORT_VALIDATION_FAILED: 'IMPORT:VALIDATION:FAILED',

    // Process: Current Account
    PROCESS_CURRENT_ACCOUNT_PROCESSING: 'PROCESS:CURRENT_ACCOUNT:PROCESSING',
    PROCESS_CURRENT_ACCOUNT_COMPLETED: 'PROCESS:CURRENT_ACCOUNT:COMPLETED',

    // Meeting: Attendance
    MEETING_ATTENDANCE_UPDATED: 'MEETING:ATTENDANCE:UPDATED',
} as const;

// Get all type values as a union type
export type WebSocketResponseType =
    typeof WEBSOCKET_MESSAGE_TYPES[keyof typeof WEBSOCKET_MESSAGE_TYPES];

// Helper function to check if a value is a valid WebSocket message type
export const isValidWebSocketType = (value: any): value is WebSocketResponseType => {
    return Object.values(WEBSOCKET_MESSAGE_TYPES).includes(value);
};

// Helper function to get all message types as an array
export const getAllWebSocketTypes = (): WebSocketResponseType[] => {
    return Object.values(WEBSOCKET_MESSAGE_TYPES);
};
